import { BugOutlined } from '@ant-design/icons';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Button, Drawer, Input, message } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  emitDevSseEvent,
  refreshDevSseEventTimestamps,
} from '@/utilities/helpers/eventStreamDevTools';

const { TextArea } = Input;

const DEV_SSE_STORAGE_KEY = 'homesight-dev-sse-payload';
const isDevelopmentBuild =
  process.env.ENABLE_DEV_SSE_STUB == 'true' && process.env.NODE_ENV === 'development';

const DEFAULT_DEV_SSE_PAYLOAD = JSON.stringify(
  {
    message_type: 'insights_smart_home_alert_event',
    source: 'device',
    utc_timestamp_inms: 1776692347974,
    message_id: '40246f7c-c4ad-40e9-8d7e-b6e617758f54',
    deviceid: '740ff170-fc3a-11f0-a7f1-ffa06c5f5322',
    recv_time: 1776692348144,
    send_time: 1776692347971,
    deviceuuid: '4a6c1820-b142-11ee-9ef7-49f691f8f242',
    providerid: 'c5da5cd0-f90c-11ee-848b-fb7c007273af',
    devicemodelid: '0db29980-a9b4-11ee-8bb5-57974a9ebc34',
    softwareversion: '10.30.03.00.919',
    groupid: 'f09f6330-f2e7-11f0-baff-31a8f5d2b67f',
    devicestatus: 'ACTIVE',
    subscriberuuid: 'f8a03140-b42d-11ee-9ef7-49f691f8f242',
    lastconnecttime: '2026-04-15T11:37:21.064Z',
    lastdisconnecttime: '2026-04-15T11:36:32.181Z',
    activationdate: '2026-03-05T20:56:15.618Z',
    basemacaddress: '6C:A6:04:D0:10:87',
    providername: 'BLR_PROVIDER',
    modelnumber: 'HC200',
    useruuid: '740ff170-fc3a-11f0-a7f1-ffa06c5f5322',
    insights_smart_home_alert_event__rule_id: 'd9f94920-3cbb-11f1-928d-bd8ad3af1bd2',
    insights_smart_home_alert_event__triggers: [
      {
        condition: {
          name: 'door_opened',
          message_type: 'device_accessory_state_update_door_window_sensor_open',
          field: 'open',
          operator: '==',
          threshold: true,
        },
        trigger: {
          value: true,
          time: 1776692347933,
          event: {
            message_type: 'device_accessory_state_update_door_window_sensor_open',
            source: 'device',
            utc_timestamp_inms: 1776692347933,
            message_id: '99d15d68-5fcc-489b-8b32-f7e0b3d7a735',
            device_accessory_state_update_door_window_sensor_open: {
              model_name: 'SBDW-002C',
              mac_address: '7C:C6:B6:96:96:4B',
              open: true,
            },
          },
        },
      },
    ],
    insights_smart_home_alert_event__display_text: 'door open rule',
    insights_smart_home_alert_event__utc_timestamp: '2026-04-20T13:39:07.971Z',
  },
  null,
  2
);

const DevSseTrigger = () => {
  const trans = useSelector(translator);
  const [open, setOpen] = useState(false);
  const [eventPayload, setEventPayload] = useState(DEFAULT_DEV_SSE_PAYLOAD);

  useEffect(() => {
    if (!isDevelopmentBuild || typeof window === 'undefined') {
      return;
    }

    const storedPayload = window.localStorage.getItem(DEV_SSE_STORAGE_KEY);

    if (storedPayload) {
      setEventPayload(storedPayload);
    }
  }, []);

  useEffect(() => {
    if (!isDevelopmentBuild || typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(DEV_SSE_STORAGE_KEY, eventPayload);
  }, [eventPayload]);

  if (!isDevelopmentBuild) {
    return null;
  }

  const handleTrigger = () => {
    try {
      const parsedEvent = JSON.parse(eventPayload);

      if (!parsedEvent || typeof parsedEvent !== 'object' || Array.isArray(parsedEvent)) {
        throw new Error('Invalid SSE payload');
      }

      if (!parsedEvent.message_type) {
        message.error(trans('DEV_SSE_TRIGGER_INVALID_JSON'));
        return;
      }

      const refreshedEventPayload = refreshDevSseEventTimestamps(parsedEvent);

      setEventPayload(JSON.stringify(refreshedEventPayload, null, 2));
      emitDevSseEvent(refreshedEventPayload);
      message.success(trans('DEV_SSE_TRIGGER_SUCCESS'));
    } catch (error) {
      message.error(trans('DEV_SSE_TRIGGER_INVALID_JSON'));
    }
  };

  return (
    <>
      <Button
        type="default"
        icon={<BugOutlined />}
        className="dev-sse-trigger-button"
        onClick={() => setOpen((previousOpen) => !previousOpen)}
      >
        {trans('DEV_SSE_TRIGGER')}
      </Button>
      <Drawer
        title={trans('DEV_SSE_TRIGGER_TITLE')}
        placement="right"
        width={560}
        open={open}
        onClose={() => setOpen(false)}
        className="dev-sse-trigger-drawer"
      >
        <div className="dev-sse-trigger-copy">{trans('DEV_SSE_TRIGGER_DESCRIPTION')}</div>
        <div className="dev-sse-trigger-label">{trans('DEV_SSE_EVENT_JSON')}</div>
        <TextArea
          rows={20}
          value={eventPayload}
          onChange={(event) => setEventPayload(event.target.value)}
          placeholder={trans('DEV_SSE_EVENT_JSON_PLACEHOLDER')}
          spellCheck={false}
        />
        <div className="dev-sse-trigger-actions">
          <Button type="primary" onClick={handleTrigger}>
            {trans('DEV_SSE_TRIGGER_FIRE')}
          </Button>
        </div>
      </Drawer>
    </>
  );
};

export default DevSseTrigger;
